<template>
  <div class="main">
    <div class="form-section">
      <h1>Get buyer feedback without going to market</h1>
      <p
        class="subheader"
      >Register your interest for the Doorsteps off-market network by leaving your name and best contact number and a member from the team will call to discuss.</p>

      <LoadingSpinner class="loading" v-if="loading" />
      <div class="form" autocomplete="off" v-else>
        <h3>Property address</h3>
        <div v-if="!manualAddressEntry">
          <AddressSearch
            :existingAddress="existingAddress"
            :gacategory="gacategory"
            :placeholderText="'Enter your property address'"
            class="addressSearchBox"
            @selectedResult="getAddress"
          />
        </div>
        <div v-else>
          <InputField
            :id="'address-manual-street'"
            :type="'text'"
            v-bind:value="formData.streetaddress"
            v-on:input="formData.streetaddress = $event"
          >Street Address</InputField>
          <SuburbSearch
            :placeholderText="'Enter your suburb or postcode'"
            class="suburbsearch ut-mt-4"
            :existingAddress="existingSuburb"
            @selectedResult="getSuburb"
          />
        </div>

        <div class="error" v-if="!isAddressValid && submitted">Please enter address details</div>

        <input class="ut-mt-4" type="checkbox" v-model="manualAddressEntry" id="manual-address" />
        <label for="manual-address"> &nbsp; Enter address manually</label>

        <h3 class="ut-mb-2">Property details</h3>

        <div class="ut-flex ut-flex-wrap">
          <RadioButton
            v-for="propertyType in propertyTypes"
            :key="propertyType"
            :id="propertyType"
            :name="propertyType"
            v-model="formData.propertytype"
            :selectedValue="formData.propertytype"
            :radioValue="propertyType"
          >{{propertyType}}</RadioButton>
        </div>

        <div class="error" v-if="!isPropertyTypeValid && submitted">Please select a property type</div>

        <div v-if="formData.propertytype !== 'Land'" class="inf-3 persist ut-mt-4">
          <div class="box ut-pr-2">
            <InputField
              :id="'property-details-beds'"
              :type="'number'"
              v-bind:value="formData.beds"
              v-on:input="formData.beds = $event"
            >Beds</InputField>
            <div class="error" v-if="!isBedsValid && submitted">Please fill in bed</div>
          </div>

          <div class="box ut-pr-2">
            <InputField
              :id="'property-details-baths'"
              :type="'number'"
              v-bind:value="formData.baths"
              v-on:input="formData.baths = $event"
            >Baths</InputField>
            <div class="error" v-if="!isBathsValid && submitted">Please fill in bath</div>
          </div>

          <div class="box">
            <InputField
              :id="'property-details-cars'"
              :type="'number'"
              v-bind:value="formData.cars"
              v-on:input="formData.cars = $event"
            >Cars</InputField>
            <div class="error" v-if="!isCarsValid && submitted">Please fill in car</div>
          </div>
        </div>

        <h3>Minimum desired price</h3>
        <div class="ut-w-full half-space">
          <InputPriceField
            :id="'asking-price'"
            :type="'number'"
            :formatting="['price']"
            v-bind:value="formData.expectedsalepricelow"
            v-on:input="formData.expectedsalepricelow = $event"
          >Asking Price ($)</InputPriceField>
        </div>

        <div class="error" v-if="!isAskingPriceValid && submitted">
          Please provide a valid minimum expected sale price.
          <br />It must be a number greater than 100000.
        </div>

        <h3>Contact details</h3>
        <div class>
          <div class="ut-w-full half-space ut-mb-4">
            <InputField
              :id="'contact-name'"
              :type="'text'"
              v-bind:value="formData.name"
              v-on:input="formData.name = $event"
            >Name</InputField>
          </div>
        </div>
        <!-- <p class="ut-text-neutral300">We'll reach out when we have buyers in your price range.</p> -->
        <div class="ut-block md:ut-flex md:ut-justify-between">
          <div class="ut-w-full half-space ut-mb-4">
            <InputField
              :id="'contact-email'"
              :type="'text'"
              v-bind:value="formData.email"
              v-on:input="formData.email = $event"
            >Email address</InputField>
          </div>
          <div class="ut-w-full half-space">
            <InputField
              id="contact-phone"
              :type="'number'"
              v-bind:value="formData.phone"
              v-on:input="formData.phone = $event"
            >Phone number</InputField>
          </div>
        </div>
        <div class="error" v-if="!isVendorNameValid && submitted">Please fill in your name</div>
        <div
          class="error"
          v-if="!isVendorEmailValid && submitted"
        >Please provide a valid email address</div>
        <div
          class="error"
          v-if="!isVendorPhoneValid && submitted"
        >Please provide a valid phone number</div>

        <div class="btnbox left ut-w-full half-space" v-if="!loading" @click="sendFormData()">
          <Button
            class="btn half-space"
            v-gaEvent="{
              category: gacategory,
              action: 'Click button - Sign up',
                }"
          >Send</Button>
        </div>
        <p class="terms-conditions">
          By pressing submit you agree to our
          <a
            :href="termsConditionsUrl"
            target="_blank"
            v-gaEvent="{
              category: gacategory,
              action: 'Click link - Terms & Conditions',
                }"
          >terms & conditions</a>
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import qs from "qs";

import ValidationHelpers from "@/helpers/validation-helpers";

import Button from "@/components/forms/Button.vue";
import InputField from "@/components/forms/InputField.vue";
import InputPriceField from "@/components/forms/InputPriceField.vue";
import RadioButton from "@/components/forms/RadioButton.vue";
import AddressSearch from "@/components/forms/search/AddressSearch.vue";
import SuburbSearch from "@/components/forms/search/SuburbSearch.vue";
import LoadingSpinner from "@/components/LoadingSpinner.vue";

export default {
  name: "Home",
  components: {
    Button,
    InputField,
    InputPriceField,
    RadioButton,
    AddressSearch,
    SuburbSearch,
    LoadingSpinner
  },
  data() {
    return {
      lead: null,
      loading: false,
      submitted: false,
      propertyTypes: ["House", "Apartment", "Land"],
      gacategory: "Off Market - Sign up",
      manualAddressEntry: false,
      existingAddress: "",
      existingSuburb: "",
      formData: {
        streetaddress: "",
        suburb: "",
        postcode: "",
        postcodeid: "",
        state: "",
        latitude: "",
        longitude: "",
        rpdataid: "",
        propertytype: "",
        beds: "",
        baths: "",
        cars: "",
        expectedsalepricelow: null,
        name: "",
        email: "",
        phone: "",
        dialleractiontypeid: 3 // Do not call
      }
    };
  },
  computed: {
    termsConditionsUrl() {
      return `${process.env.VUE_APP_OAWEB}/terms`;
    },
    isAddressValid() {
      return (
        !this.isEmpty(this.formData.streetaddress) &&
        !this.isEmpty(this.formData.suburb)
      );
    },
    isPropertyTypeValid() {
      return !this.isEmpty(this.formData.propertytype);
    },
    isBedsValid() {
      return (
        this.formData.propertytype === "Land" ||
        !this.isEmpty(this.formData.beds)
      );
    },
    isBathsValid() {
      return (
        this.formData.propertytype === "Land" ||
        !this.isEmpty(this.formData.baths)
      );
    },
    isCarsValid() {
      return (
        this.formData.propertytype === "Land" ||
        !this.isEmpty(this.formData.cars)
      );
    },
    isAskingPriceValid() {
      return (
        !this.isEmpty(this.formData.expectedsalepricelow) &&
        !isNaN(this.formData.expectedsalepricelow) &&
        this.formData.expectedsalepricelow >= 1000 &&
        this.formData.expectedsalepricelow <= 30000000
      );
    },
    isVendorNameValid() {
      return !this.isEmpty(this.formData.name);
    },
    isVendorEmailValid() {
      return (
        !this.isEmpty(this.formData.email) &&
        ValidationHelpers.emailValidation(this.formData.email)
      );
    },
    isVendorPhoneValid() {
      return (
        !this.isEmpty(this.formData.phone) &&
        ValidationHelpers.phoneValidation(this.formData.phone)
      );
    },
    isFormValid() {
      return (
        this.isAddressValid &&
        this.isPropertyTypeValid &&
        this.isBedsValid &&
        this.isBathsValid &&
        this.isCarsValid &&
        this.isAskingPriceValid &&
        this.isVendorNameValid &&
        this.isVendorEmailValid &&
        this.isVendorPhoneValid
      );
    }
  },
  methods: {
    isEmpty(value) {
      return value === null || value === undefined || value.length === 0;
    },
    getSuburb(selected) {
      console.log(selected);
      this.formData.suburb = selected.suburb;
      this.formData.postcode = selected.postcode;
      this.formData.postcodeid = selected.id;
      this.formData.state = selected.state;
      this.formData.latitude = selected.latitude;
      this.formData.longitude = selected.longitude;
    },
    getAddress(selected) {
      console.log(selected);
      this.formData.streetaddress = selected.details.streetaddress;
      this.formData.suburb = selected.details.suburb;
      this.formData.postcode = selected.details.postcode;
      this.formData.state = selected.details.state;
      this.formData.rpdataid = selected.details.id;
      this.formData.latitude = selected.details.latitude;
      this.formData.longitude = selected.details.longitude;

      this.getPostcodeId(this.formData.suburb);
    },
    getPostcodeId(suburb) {
      axios
        .get(`${process.env.VUE_APP_API_URL}/postcoderest/find/${suburb}`)
        .then(res => {
          this.formData.postcodeid = res.data.data[0].id;
        })
        .catch(e => {
          console.log(e);
        });
    },
    sendFormData() {
      this.submitted = true;

      if (this.isFormValid) {
        this.loading = true;
        this.priceFrom = Number(this.priceFrom);
        

        this.getLeadWithFormData().then(() => {
          this.loading = false;
          this.$emit("signed-up", this.lead);
          // this.updateLeadStage().then(() => {
          // });
        });
      }
    },
    getLeadWithFormData() {
      return this.createLead();
    },
    updateLead() {
      return axios.put(
        `${process.env.VUE_APP_API_URL}/leadrest/update-lead/${this.lead.key}`,
        qs.stringify(this.formData)
      );
    },
    createLead() {
      return new Promise((resolve, reject) => {
        this.formData.ch = 100;
        axios
          .post(
            `${process.env.VUE_APP_API_URL}/interactionrest`,
            qs.stringify(this.formData)
          )
          .then(interactionRes => {
            this.lead = {
              firstname : this.formData.name
            };

            resolve(true);
            this.$emit("signed-up", this.lead);
            // axios
            //   .post(
            //     `${process.env.VUE_APP_API_URL}/interactionrest/${interactionRes.data.data.key}/lead`,
            //     qs.stringify(this.formData)
            //   )
            //   .then(leadRes => {
            //     this.lead = leadRes.data.data;
            //     resolve(true);
            //   })
            //   .catch(err => reject(err));
          })
          .catch(err => reject(err));
      });
    },
    updateLeadStage() {
      const stageData = {
        leadstagetypeid: 22, // Off market
        oauserkey: this.lead.userkey,
        sourcetypeid: 2, // Vendor
        platformtypeid: 5 // OAWeb
      };

      return axios.put(
        `${process.env.VUE_APP_API_URL}/leadrest/update-lead-stage/${this.lead.key}`,
        qs.stringify(stageData)
      );
    }
  },
  created() {
    if (this.$route.query.leadKey) {
      this.loading = true;
      axios
        .get(
          `${process.env.VUE_APP_API_URL}/leadrest/lead-by-key/${this.$route.query.leadKey}`
        )
        .then(res => {
          console.log(res);

          this.lead = res.data.data;
          this.formData.streetaddress = this.lead.streetaddress || "";
          this.formData.state = this.lead.state || "";
          this.formData.suburb = this.lead.suburb || "";
          this.formData.postcode = this.lead.postcode || "";
          this.formData.beds = this.lead.beds || "";
          this.formData.postcodeid = this.lead.postcodeid || "";
          this.formData.propertytype = this.lead.propertytype || "";
          this.formData.expectedsalepricelow = this.lead.expectedsalepricelow
            ? this.lead.expectedsalepricelow.substring(
                0,
                this.lead.expectedsalepricelow.indexOf(".")
              )
            : "";

          this.formData.name =
            this.lead.firstname + this.lead.lastname
              ? " " + this.lead.lastname
              : "";
          this.formData.email = this.lead.email || "";
          this.formData.phone = this.lead.phone || "";

          this.existingSuburb = `${this.formData.suburb}, ${this.formData.state} ${this.formData.postcode}`;
          this.existingAddress = `${this.formData.streetaddress}, ${this.formData.suburb} ${this.formData.state} ${this.formData.postcode}`;

          this.loading = false;
        })
        .catch(e => {
          console.log(e);
          this.loading = false;
        });
    }
  }
};
</script>

<style scoped lang="scss">
.main {
  h1 {
    text-align: center;
    font-size: 32px;
    font-weight: 900;
    line-height: 40px;
    margin: 20px 0;
  }

  h3 {
    font-weight: 900;
    margin-top: 40px;
  }

  .header {
    background-color: #fff;
  }

  .form-section {
    padding: 10px 20px 30px;
    background-color: #fff;
    top: 300px;
    margin-bottom: 50px;

    .subheader {
      text-align: center;
    }

    .form {
      margin: 0 auto;

      .btnbox {
        margin: 0 auto;
        .btn {
          @apply ut-bg-green400 ut-w-full;
          margin: 40px auto 0;
        }
        &.left {
          margin: 0px 0 0;
        }
      }

      .inf-3 {
        display: block;

        &.persist {
          display: flex;
          justify-content: space-between;
          .box {
            // padding: 0 10px 0 10px;
          }
        }
      }

      .error {
        color: #f83c5b;
      }

      .terms-conditions {
        @apply ut-text-neutral300 ut-italic;
        a {
          text-decoration: underline;
        }
      }
    }
  }
}

@screen md {
  .main {
    min-width: 500px;
    padding-bottom: 100px;
    position: relative;

    h1 {
      margin: 40px 0;
    }

    h3 { margin-bottom:10px }

    .header {
      position: absolute;
      width: 85%;
      height: 50px;
      min-width: 850px;
      left: 0;
      right: 0;
      margin: auto;
    }

    // .radio-group span:after {
    //       border-radius: 9999px;
    //       position: absolute;
    //       visibility: visible;
    //       content: '';
    //       top: 10px;
    //       left: 0px;
    //       right: 0px;
    //       margin: auto;
    //       height: 12px;
    //       width: 12px;
    //   }

    .form-section {
      position: absolute;
      z-index: 10;
      width: 650px;
      left: 50%;
      padding: 10px 80px 30px;

      left: 0;
      right: 0;
      margin: auto;
      border-radius: 3px;
      box-shadow: -1px 12px 20px 0 rgba(164, 158, 158, 0.5);

      .subheader {
        text-align: center;
        width: 100%;
      }

      .form {
        width: 520px;

        .half-space {
          width: 49%;
        }

        .inf-3 {
          display: flex;
          justify-content: space-between;
          .box {
            // padding: 0 10px 0 10px;
          }
        }

        .btnbox {
          .btn {
            @apply ut-w-full;
            margin: 30px auto 0;
          }
        }
      }
    }
  }
}
</style>
